/**
 * CL-405B — Professional CMA workspace sub-navigation and legacy redirect helpers.
 */

import { normalizeCmaBuilderStep } from "./cmaBuilderSteps";

function safeStr(value) {
  return String(value ?? "").trim();
}

export const PROFESSIONAL_CMA_MODES = {
  ESTIMATE: "estimate",
  BUILDER: "builder",
  PUBLISHED: "published",
  REQUEST: "request",
};

export const CLIENT_PROFESSIONAL_CMA_MODES = [
  { id: PROFESSIONAL_CMA_MODES.PUBLISHED, label: "Published report" },
  { id: PROFESSIONAL_CMA_MODES.REQUEST, label: "Request update" },
];

export const ADMIN_PROFESSIONAL_CMA_MODES = [
  { id: PROFESSIONAL_CMA_MODES.ESTIMATE, label: "Market Estimate" },
  { id: PROFESSIONAL_CMA_MODES.BUILDER, label: "Builder" },
  { id: PROFESSIONAL_CMA_MODES.PUBLISHED, label: "Published Reports" },
];

const CLIENT_MODE_IDS = new Set(CLIENT_PROFESSIONAL_CMA_MODES.map((m) => m.id));
const ADMIN_MODE_IDS = new Set(ADMIN_PROFESSIONAL_CMA_MODES.map((m) => m.id));

const LEGACY_PCMA_MODE_ALIASES = {
  market_estimate: PROFESSIONAL_CMA_MODES.ESTIMATE,
  valuation: PROFESSIONAL_CMA_MODES.ESTIMATE,
  workbench: PROFESSIONAL_CMA_MODES.BUILDER,
  cma_workbench: PROFESSIONAL_CMA_MODES.BUILDER,
  marks_valuation: PROFESSIONAL_CMA_MODES.PUBLISHED,
  published_report: PROFESSIONAL_CMA_MODES.PUBLISHED,
  published_reports: PROFESSIONAL_CMA_MODES.PUBLISHED,
  request: PROFESSIONAL_CMA_MODES.REQUEST,
  request_review: PROFESSIONAL_CMA_MODES.REQUEST,
};

export function normalizeProfessionalCmaMode(value, { adminMode = false } = {}) {
  const key = safeStr(value).toLowerCase();
  const resolved = LEGACY_PCMA_MODE_ALIASES[key] || key;
  const allowed = adminMode ? ADMIN_MODE_IDS : CLIENT_MODE_IDS;
  if (allowed.has(resolved)) return resolved;
  if (resolved === PROFESSIONAL_CMA_MODES.ESTIMATE && !adminMode) {
    return PROFESSIONAL_CMA_MODES.PUBLISHED;
  }
  if (resolved === PROFESSIONAL_CMA_MODES.BUILDER && !adminMode) {
    return PROFESSIONAL_CMA_MODES.PUBLISHED;
  }
  return adminMode ? PROFESSIONAL_CMA_MODES.BUILDER : PROFESSIONAL_CMA_MODES.PUBLISHED;
}

export function professionalCmaModesForRole(adminMode = false) {
  return adminMode ? ADMIN_PROFESSIONAL_CMA_MODES : CLIENT_PROFESSIONAL_CMA_MODES;
}

/**
 * Resolve legacy tab/section combinations into canonical Professional CMA workspace URLs.
 * Returns pathname+search string or null when no redirect is needed.
 */
export function resolveLegacyPropertyWorkspaceRedirect(searchParams, { adminMode = false, pathname = "" } = {}) {
  const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
  const tab = safeStr(params.get("tab")).toLowerCase();
  const section = safeStr(params.get("section")).toLowerCase();

  const next = new URLSearchParams(params);
  let changed = false;

  if (tab === "cma_workbench") {
    next.set("tab", "market_sale");
    next.set("marketPane", "professional_cma");
    next.set("pcmaMode", PROFESSIONAL_CMA_MODES.BUILDER);
    changed = true;
  }

  if (tab === "intelligence" && section === "valuation") {
    next.set("tab", "market_sale");
    next.set("marketPane", "professional_cma");
    next.delete("section");
    next.set(
      "pcmaMode",
      adminMode ? PROFESSIONAL_CMA_MODES.ESTIMATE : PROFESSIONAL_CMA_MODES.PUBLISHED
    );
    if (adminMode && !next.get("cmaStep")) next.set("cmaStep", "subject");
    else next.delete("cmaStep");
    changed = true;
  }

  if (tab === "intelligence" && section === "marks_valuation") {
    next.set("tab", "market_sale");
    next.set("marketPane", "professional_cma");
    next.delete("section");
    next.set("pcmaMode", PROFESSIONAL_CMA_MODES.PUBLISHED);
    next.delete("cmaStep");
    changed = true;
  }

  if (tab === "valuation" || tab === "marks_valuation") {
    next.set("tab", "market_sale");
    next.set("marketPane", "professional_cma");
    next.set(
      "pcmaMode",
      tab === "marks_valuation" || !adminMode
        ? PROFESSIONAL_CMA_MODES.PUBLISHED
        : PROFESSIONAL_CMA_MODES.ESTIMATE
    );
    changed = true;
  }

  if (changed) {
    const base = safeStr(pathname) || "";
    const query = next.toString();
    return query ? `${base}?${query}` : base;
  }

  return null;
}

export function buildProfessionalCmaWorkspaceQuery({
  pcmaMode = null,
  cmaStep = null,
  markValuationId = null,
  requestId = null,
  adminMode = false,
} = {}) {
  const params = {};
  const mode = safeStr(pcmaMode);
  if (mode) params.pcmaMode = normalizeProfessionalCmaMode(mode, { adminMode });
  if (cmaStep) params.cmaStep = normalizeCmaBuilderStep(cmaStep);
  if (markValuationId) params.markValuationId = safeStr(markValuationId);
  if (requestId) params.requestId = safeStr(requestId);
  return params;
}
